import { createContext, useContext, type ReactNode } from "react";

/**
 * The service that `useServiceId()` returns below this provider, instead of the
 * `serviceId` route param.
 *
 * The branch Explorer is the only caller: on a branch route, `serviceId` is the
 * parent service and `branchId` is the branch, and the Explorer reads the
 * branch's own data. Without a provider, `useServiceId()` reads the route, so
 * every service page behaves as before.
 *
 * The scope is the subtree, so anything mounted above the outlet keeps reading
 * the parent on a branch route. That is deliberate: the app chrome (breadcrumb,
 * command palette, the SQL engine) talks to the parent service, and only the
 * Explorer content talks to the branch. A component that must reach the parent
 * from inside the branch Explorer has to read the route, not this hook.
 */
const ServiceIdOverrideContext = createContext<string | undefined>(undefined);

export const ServiceIdOverrideProvider = ({
  serviceId,
  children,
}: {
  serviceId: string;
  children: ReactNode;
}) => (
  <ServiceIdOverrideContext.Provider value={serviceId}>
    {children}
  </ServiceIdOverrideContext.Provider>
);

export const useServiceIdOverride = () => useContext(ServiceIdOverrideContext);
